import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListMusic } from '@fortawesome/pro-light-svg-icons/faListMusic';
import { TypedMemo } from '../../common/types';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { useFollowPlaylist } from '../hooks/useFollowPlaylist';
import { PlaylistsTable, PLAYLISTS_EXPORT_FILENAME } from '../components/PlaylistsTable/PlaylistsTable';
import { ExportCsvButton } from '../components/ExportCsvButton/ExportCsvButton';
import { MeiliPlaylistData, PlaylistSearchField } from '../components/SearchField';
import { WatchlistSection } from './WatchlistSection';
import { WatchlistPagination } from './WatchlistPagination';
import { WATCHLIST_PAGE_SIZE } from './watchlistPageSize';

interface WatchlistPlaylistsPanelProps {
  selectedDateValue: string;
}

/** The followed playlists, with search-to-follow and CSV export. */
const WatchlistPlaylistsPanelComponent: React.FC<WatchlistPlaylistsPanelProps> = ({ selectedDateValue }) => {
  const intl = useIntl();
  const trpcMusicAPI = useTRPCAPI();
  const { showSuccess, showError } = useShowToast();
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useTRPCQuery(
    trpcMusicAPI.playlists.listFollowed.queryOptions({
      offset: (page - 1) * WATCHLIST_PAGE_SIZE,
      limit: WATCHLIST_PAGE_SIZE,
    })
  );

  const { followPlaylist, isPending } = useFollowPlaylist({
    onSuccess: () => {
      showSuccess(
        intl.formatMessage({
          id: 'app.sml.playlists.follow.success',
          defaultMessage: 'Playlist added to your followed list',
        })
      );
    },
    onError: (followError) => {
      showError(
        followError.message ||
          intl.formatMessage({ id: 'app.sml.playlists.follow.error', defaultMessage: 'Failed to follow playlist' })
      );
    },
  });

  const onSelected = useCallback((playlist: MeiliPlaylistData) => followPlaylist(playlist.id), [followPlaylist]);
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return (
    <WatchlistSection
      icon={<FontAwesomeIcon icon={faListMusic} />}
      title={intl.formatMessage({ id: 'app.sml.home.tab.playlists', defaultMessage: 'Playlists' })}
      exportButton={
        <ExportCsvButton kind="playlists" filename={PLAYLISTS_EXPORT_FILENAME} isEmpty={(data?.total ?? 0) === 0} />
      }
      search={
        <Row className="mb-3">
          <Col xs={12} md={6} lg={5}>
            <PlaylistSearchField onPlaylistSelected={onSelected} isDisabled={isPending} />
          </Col>
        </Row>
      }
      error={error}
      retry={retry}
    >
      <PlaylistsTable playlists={data?.playlists ?? []} isLoading={isLoading} selectedDateValue={selectedDateValue} />
      <WatchlistPagination
        page={page}
        onPageChange={setPage}
        hasNext={data?.hasMore === true}
        total={data?.total ?? 0}
        entityLabel={intl.formatMessage({ id: 'app.sml.home.entity.playlists', defaultMessage: 'playlists' })}
      />
    </WatchlistSection>
  );
};

export const WatchlistPlaylistsPanel = TypedMemo(WatchlistPlaylistsPanelComponent);
